import classNames from 'classnames';
import { Field, Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCheckoutState } from '../../../utils/checkoutState';
import { config } from '../../../utils/config';
import getValidator from '../../../utils/getValidator';
import { pushErrorTrackingEvent } from '../../../utils/tracking';
import Button from '../../atoms/Button';
import Price from '../../atoms/Price';
import InputWithIconAndButtonField from '../../formik/InputWithIconAndButtonField';
import { showDefaultError, showNotification } from '../Notifications';
import './style.scss';

const GiftCardList = ({ isDisabled }: { isDisabled: boolean }) => {
  const { checkoutState, addGiftCard, removeGiftCard } = useCheckoutState();
  const { formatMessage } = useIntl();
  const [isLoading, setLoading] = useState({ add: false, remove: false, id: '' });
  const { giftCards = [] } = checkoutState.orderState.payment || { giftCards: [] };

  const { maxLength, pin } = config.features.giftCards;
  const useGiftCardWithPin = pin.enabled;
  const isVouchersEnabled = config.features.vouchers.enabled;

  const initialValues = {
    giftCardCode: '',
    giftCardPin: '',
  };

  const statusCodeI18nKeys = {
    204: 'payment.giftCard.notifications.removed',
    404: 'payment.giftCard.notifications.notFound',
    406: 'payment.giftCard.notifications.noBalance',
    409: 'payment.giftCard.notifications.alreadyActive',
  };

  const statusCodeTrackingEventAction = {
    404: 'general form',
  };

  const addHandler = async (
    { giftCardCode, giftCardPin }: typeof initialValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  ) => {
    if (giftCardCode === '') {
      return;
    }
    setLoading({ add: true, remove: false, id: '' });
    try {
      const _giftCards = await addGiftCard({ giftCardCode, giftCardPin });
      setFieldValue('giftCardCode', '');
      setFieldValue('giftCardPin', '');
      if (_giftCards?.length) {
        const giftCard = _giftCards.find(
          _giftCard => _giftCard.recurringPaymentInformation?.giftCardCode === giftCardCode
        );
        if (giftCard) {
          showNotification({
            level: 'success',
            text: formatMessage(
              { id: 'payment.giftCard.notifications.success' },
              { value: giftCard.recurringPaymentInformation?.remainingAmount }
            ),
            singleInstance: true,
            reference: 'gift-card',
          });
        }
      }
    } catch (e: any) {
      const error = statusCodeI18nKeys[e.statusCode];
      if (error) {
        const message = formatMessage({ id: error });
        pushErrorTrackingEvent(statusCodeTrackingEventAction[e.statusCode], 'error', message);

        showNotification({
          level: 'error',
          text: message,
          autoClose: true,
          closable: true,
          singleInstance: true,
          reference: 'gift-card',
        });
      } else {
        showDefaultError();
      }
    }
    setLoading({ add: false, remove: false, id: '' });
  };

  const removeHandler = async ({ id }: { id: string }) => {
    setLoading({ add: false, remove: true, id });
    try {
      await removeGiftCard({ id });
      showNotification({
        level: 'success',
        text: formatMessage({ id: 'payment.giftCard.notifications.removed' }),
        singleInstance: true,
        reference: 'gift-card',
      });
    } catch (e: any) {
      const error = statusCodeI18nKeys[e.statusCode];
      if (error) {
        const message = formatMessage({ id: error });
        showNotification({
          level: 'error',
          text: message,
          autoClose: true,
          closable: true,
          singleInstance: true,
          reference: 'gift-card',
        });
      } else {
        showDefaultError();
      }
    }
    setLoading({ add: false, remove: false, id: '' });
  };

  const giftCardCodeValidationRules = getValidator(config.features.giftCards);

  return (
    <div className="gift-card">
      {!!giftCards.length && (
        <div className="gift-card__list">
          {giftCards.map(({ recurringPaymentOptionId, recurringPaymentInformation }) => (
            <div className="gift-card__container" key={recurringPaymentOptionId}>
              <span className="gift-card__name">{recurringPaymentInformation?.giftCardCode}</span>
              <span className="gift-card__discount">
                <Price price={recurringPaymentInformation.remainingAmount} isLarge />
              </span>
              <Button
                type="button"
                className="gift-card__btn"
                onClick={() => removeHandler({ id: recurringPaymentOptionId })}
                isLoading={isLoading.remove && recurringPaymentOptionId === isLoading.id}
                disabled={isDisabled}
              >
                <FormattedMessage id="generic.remove" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {!giftCards.length && (
        <Formik
          initialValues={initialValues}
          onSubmit={(values, { setFieldValue }) => addHandler(values, setFieldValue)}
        >
          {({ submitForm, errors }) => (
            <div
              className={classNames('gift-card__input', {
                'gift-card__input--with-error': (errors?.giftCardCode || errors?.giftCardPin) && isVouchersEnabled,
                'gift-card__input--with-gift-cards': !!giftCards?.length,
                'gift-card__input--with-vouchers': isVouchersEnabled,
                'gift-card__input--with-pin': useGiftCardWithPin,
                'gift-card__input--with-pin-and-error':
                  useGiftCardWithPin && (errors?.giftCardCode || errors?.giftCardPin),
              })}
            >
              <Field
                component={InputWithIconAndButtonField}
                button={
                  useGiftCardWithPin ? undefined : { text: 'generic.add', onClick: submitForm, disabled: isDisabled }
                }
                placeholder={formatMessage({ id: 'payment.giftCard.label' })}
                className="gift-card__code"
                name="giftCardCode"
                isLoading={isLoading.add}
                maxLength={maxLength}
                validate={giftCardCodeValidationRules}
                isInputStatusIconDisabled
              />
              {useGiftCardWithPin && (
                <Field
                  component={InputWithIconAndButtonField}
                  button={{ text: 'generic.add', onClick: submitForm, disabled: isDisabled }}
                  placeholder={formatMessage({ id: 'payment.giftCard.pin.label' })}
                  className="gift-card__pin"
                  name="giftCardPin"
                  isLoading={isLoading.add}
                  maxLength={pin.maxLength}
                  validate={getValidator(pin)}
                  isInputStatusIconDisabled
                />
              )}
            </div>
          )}
        </Formik>
      )}
    </div>
  );
};

export default GiftCardList;
